import type { PublicEvent, PublicGiftIndexResponse } from "@/services/api/publicEvents";

export interface GiftCardPaymentGate {
  /** false ONLY when the payload explicitly says card payments are unavailable. */
  cardPaymentsAllowed: boolean;
  /** Connected account for Stripe.js init (direct-charge mode only), else null. */
  stripeAccount: string | null;
}

/**
 * JUB-53: derive the card-payment gate from the public gifts payload.
 *
 * Only an explicit `cardPaymentsEnabled: false` gates the card flow — an
 * undefined flag (legacy payloads, authenticated draft-preview endpoint)
 * keeps the pre-JUB-53 behavior so previews don't regress.
 */
export function resolveGiftCardPaymentGate(
  payload?: Pick<PublicGiftIndexResponse, "cardPaymentsEnabled" | "stripeAccountId"> | null
): GiftCardPaymentGate {
  return {
    cardPaymentsAllowed: payload?.cardPaymentsEnabled !== false,
    stripeAccount: payload?.stripeAccountId ?? null,
  };
}

export type ContributionRecipientKind = "wedding" | "baptism" | "generic";

export interface ContributionRecipient {
  kind: ContributionRecipientKind;
  names: string;
}

export type ContributionRecipientEvent = Pick<
  PublicEvent,
  | "name"
  | "starBride"
  | "starGroom"
  | "starBaptism"
  | "starBaptism2"
  | "includesWedding"
  | "includesBaptism"
>;

/**
 * JUB-79: who receives the guest's money — shown on the contribution form
 * ("Contribuția ta ajunge direct în contul mirilor {names}") and in the
 * post-payment confirmation view.
 *
 * Mirrors the backend derivation in server/app/Event.php
 * (Event::contributionRecipient): wedding → bride & groom, botez → baptism
 * star(s), fallback → event name.
 */
export function getContributionRecipient(event: ContributionRecipientEvent): ContributionRecipient {
  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;

  if (includesWedding && event.starBride && event.starGroom) {
    return {
      kind: "wedding",
      names: `${event.starBride.trim()} & ${event.starGroom.trim()}`,
    };
  }

  if (includesBaptism) {
    const names = [event.starBaptism, event.starBaptism2]
      .map((n) => n?.trim())
      .filter((n): n is string => !!n);

    if (names.length > 0) {
      return { kind: "baptism", names: names.join(" & ") };
    }
  }

  return { kind: "generic", names: event.name };
}

/** i18n key for the recipient identity line, per recipient kind. */
export function contributionRecipientKey(kind: ContributionRecipientKind): string {
  switch (kind) {
    case "wedding":
      return "gifts.payment.recipientWedding";
    case "baptism":
      return "gifts.payment.recipientBaptism";
    default:
      return "gifts.payment.recipientGeneric";
  }
}
